import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/api";
import { ChevronRight, Chrome, MousePointer2, Plus, Sparkles, Lock, Globe, Zap, Download, FolderOpen, ToggleRight, Settings2, LifeBuoy, Mail } from "lucide-react";

const DOWNLOAD_URL = `${BACKEND_URL}/api/clipper/download`;

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9CA47E] mb-3">{children}</div>
);

const codeBlock =
  "font-mono text-[12px] leading-relaxed bg-[#333333] text-white/85 p-5 overflow-x-auto whitespace-pre";

export default function Clipper() {
  return (
    <div className="text-[#333333] min-h-screen">
      {/* Hero */}
      <section className="bg-white border-b border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-7">
            <SectionLabel>◇ Browser extension · v0.1 · MV3</SectionLabel>
            <h1 className="font-heading font-medium text-5xl sm:text-6xl tracking-tight leading-[0.98]">
              VoraStyle <span className="italic">Clipper</span>.
            </h1>
            <p className="font-body text-[15px] sm:text-base text-[#595959] mt-5 max-w-xl leading-relaxed">
              An elevated clipper for curators. Right-click any product image on any retailer, choose
              <em> Clip image to VoraStyle</em>, and it lands in your library — ready to drag onto your canvas.
              Chrome / Edge / Brave today, Safari + Firefox in the works.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <a
                data-testid="clipper-install-btn"
                href={DOWNLOAD_URL}
                download="vorastyle-clipper.zip"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[12px] uppercase tracking-[0.18em]"
              >
                <Download className="w-3.5 h-3.5" /> Download Clipper (ZIP)
              </a>
              <a
                href="#install"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#333333] hover:bg-[#333333] hover:text-white font-mono text-[12px] uppercase tracking-[0.18em] transition-colors"
              >
                <Chrome className="w-3.5 h-3.5" /> How to install
              </a>
              <Link
                data-testid="clipper-back-products"
                to="/products"
                className="self-center font-mono text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 ml-1 text-[#595959]"
              >
                Back to library →
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">
              <span className="inline-flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#9CA47E]" /> One-click clip</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-[#9CA47E]" /> JWT auth</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="w-3 h-3 text-[#9CA47E]" /> Works on any URL</span>
            </div>
          </div>

          {/* Mock browser frame */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative bg-white border border-[#333333] shadow-sm">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#F2EDE3] border-b border-[#E5E5E5]">
                <span className="w-2 h-2 rounded-full bg-[#9CA47E]" />
                <span className="w-2 h-2 rounded-full bg-[#333333]/30" />
                <span className="w-2 h-2 rounded-full bg-[#333333]/30" />
                <div className="ml-3 flex-1 font-mono text-[10px] text-[#595959] uppercase tracking-[0.18em] truncate">
                  shop.example.com/product/red-velvet-armchair
                </div>
                <div className="flex items-center gap-1 px-2 py-1 bg-[#333333] text-white font-mono text-[9px] uppercase tracking-[0.18em]">
                  <Plus className="w-2.5 h-2.5" /> Clipper
                </div>
              </div>
              <div className="aspect-[4/3] bg-[#F2EDE3] flex items-center justify-center relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&auto=format"
                  alt=""
                  className="max-w-[80%] max-h-[80%] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                {/* fake right-click menu */}
                <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-2 bg-white border border-[#333333] shadow-md min-w-[220px]">
                  <div className="px-3 py-2 text-xs font-body border-b border-[#E5E5E5]">Save image as…</div>
                  <div className="px-3 py-2 text-xs font-body border-b border-[#E5E5E5]">Copy image</div>
                  <div className="px-3 py-2 text-xs font-body bg-[#9CA47E] text-white inline-flex items-center gap-2 w-full">
                    <Sparkles className="w-3 h-3" /> Clip image to VoraStyle
                  </div>
                  <div className="px-3 py-2 text-xs font-body">Inspect</div>
                </div>
                <MousePointer2 className="absolute top-1/2 left-1/2 w-4 h-4 -translate-x-1 -translate-y-2 text-[#333333]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <SectionLabel>§ How it works</SectionLabel>
        <h2 className="font-heading text-4xl tracking-tight">Three ways to clip.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            ["01", "Right-click an image", "Pick \"Clip image to VoraStyle\" from the context menu. The product lands in your library, tagged with the page URL."],
            ["02", "Floating button", "A small \"+ Clip to VoraStyle\" appears on every page. One click, no menu — auto-picks the largest <img>."],
            ["03", "Toolbar popup", "Open the extension popup, sign in once, hit \"Clip current page\" — uses og:image + page metadata."],
          ].map(([n, t, d]) => (
            <div key={n} className="bg-white border border-[#EFEFEF] p-6">
              <div className="font-mono text-[11px] tracking-[0.2em] text-[#9CA47E]">{n}</div>
              <div className="font-heading text-2xl mt-3">{t}</div>
              <p className="font-body text-sm text-[#595959] mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How to install */}
      <section id="install" className="bg-white border-t border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-4">
              <SectionLabel>◇ Install in 60 seconds</SectionLabel>
              <h2 className="font-heading text-4xl tracking-tight leading-[1.05]">
                How to install <span className="italic">the Clipper</span>.
              </h2>
              <p className="font-body text-[#595959] mt-4 leading-relaxed">
                Until we ship to the Chrome Web Store, the Clipper installs as an unpacked extension via Chrome's Developer Mode. Five steps, no command line, no account.
              </p>
              <a
                data-testid="install-download-2"
                href={DOWNLOAD_URL}
                download="vorastyle-clipper.zip"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[12px] uppercase tracking-[0.18em]"
              >
                <Download className="w-3.5 h-3.5" /> Download ZIP
              </a>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959]">
                ~14 KB · Manifest V3 · Source visible
              </div>
            </div>

            <div className="col-span-12 lg:col-span-8 space-y-3">
              {[
                {
                  n: "01",
                  icon: <Download className="w-4 h-4" />,
                  title: "Download the ZIP",
                  body: <>Click <strong>Download Clipper (ZIP)</strong> above. Save it anywhere on your computer.</>,
                },
                {
                  n: "02",
                  icon: <FolderOpen className="w-4 h-4" />,
                  title: "Unzip the file",
                  body: <>Double-click <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5">vorastyle-clipper.zip</code> to extract it. You'll get a folder with <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5">manifest.json</code>, <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5">background.js</code>, and friends inside.</>,
                },
                {
                  n: "03",
                  icon: <Settings2 className="w-4 h-4" />,
                  title: "Open Chrome Extensions",
                  body: <>In Chrome / Edge / Brave / Arc, paste <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5">chrome://extensions</code> into the address bar and press Enter.</>,
                },
                {
                  n: "04",
                  icon: <ToggleRight className="w-4 h-4" />,
                  title: "Enable Developer mode",
                  body: <>Flip the <strong>Developer mode</strong> toggle in the top-right corner of the extensions page.</>,
                },
                {
                  n: "05",
                  icon: <FolderOpen className="w-4 h-4" />,
                  title: "Load unpacked → pick the folder",
                  body: <>Click <strong>Load unpacked</strong> (top-left), select the unzipped <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5">vorastyle-clipper</code> folder, and you're done. Pin the icon to your toolbar.</>,
                },
                {
                  n: "06",
                  icon: <Sparkles className="w-4 h-4" />,
                  title: "Sign in & clip",
                  body: <>Click the toolbar icon → enter your VoraStyle email + password → right-click any image on any retailer → <em>Clip image to VoraStyle</em>.</>,
                },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-[64px_36px_1fr] items-start gap-4 bg-[#F2EDE3] border border-[#EFEFEF] p-4 sm:p-5">
                  <div className="font-mono text-[#9CA47E] text-[12px] tracking-[0.2em] pt-0.5">{s.n}</div>
                  <div className="w-9 h-9 bg-white border border-[#333333] flex items-center justify-center text-[#333333]">
                    {s.icon}
                  </div>
                  <div>
                    <div className="font-heading text-xl">{s.title}</div>
                    <div className="font-body text-sm text-[#595959] mt-1 leading-relaxed">{s.body}</div>
                  </div>
                </div>
              ))}

              <div className="bg-[#333333] text-white p-5 mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9CA47E]">⚠ Note</div>
                <div className="font-body text-sm mt-2 leading-relaxed">
                  Chrome will show a yellow "Developer mode extensions" banner — that's normal for unpacked extensions and goes away once we ship the public Web Store build. Source is plain JavaScript: feel free to inspect <code className="font-mono text-[12px] bg-white/10 px-1.5">background.js</code> before installing.
                </div>
              </div>

              {/* Support callout */}
              <div data-testid="clipper-support" className="mt-4 grid grid-cols-[44px_1fr_auto] items-center gap-4 bg-white border border-[#333333] p-5">
                <div className="w-11 h-11 bg-[#9CA47E] text-white flex items-center justify-center">
                  <LifeBuoy className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading text-lg leading-tight">Having trouble downloading or installing?</div>
                  <div className="font-body text-sm text-[#595959] mt-1 leading-relaxed">
                    We're here to help — drop us a line and a real human will reply.
                  </div>
                </div>
                <a
                  data-testid="clipper-support-email"
                  href="mailto:Support@VoraStyle.com?subject=Clipper%20install%20help"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#333333] hover:bg-[#333] text-white font-mono text-[11px] uppercase tracking-[0.18em]"
                >
                  <Mail className="w-3.5 h-3.5" /> Support@VoraStyle.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="bg-white border-y border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <SectionLabel>⌗ Architecture</SectionLabel>
            <h2 className="font-heading text-4xl tracking-tight">Clean MV3, no surprises.</h2>
            <p className="font-body text-[#595959] mt-4 leading-relaxed">
              The extension is a Manifest V3 service-worker design. Sign in once → JWT lives in
              <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5 py-0.5 mx-0.5">chrome.storage.local</code>
              → every clip is a single <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5 py-0.5 mx-0.5">POST /api/products/clip-image</code>
              with <code className="font-mono text-[12px] bg-[#F2EDE3] px-1.5 py-0.5 mx-0.5">Authorization: Bearer</code>.
              No analytics, no background tracking, no surprises.
            </p>
            <ul className="mt-6 space-y-2 font-body text-sm">
              {[
                "Manifest V3 service worker (no legacy event pages)",
                "Shadow-DOM injected button (host-page CSS-isolated)",
                "Right-click menus on images, pages, links",
                "Backend endpoint: POST /api/products/clip-image",
                "Fallback: server-side scrape via /api/products/clip",
              ].map((f) => (
                <li key={f} className="flex gap-3"><ChevronRight className="w-4 h-4 mt-0.5 text-[#9CA47E]" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <pre className={codeBlock}>{`// background.js — handles right-click clip
chrome.contextMenus.onClicked.addListener(async (info, tab) => {
  const meta = await readPageMeta(tab.id);
  await fetch(\`\${api}/api/products/clip-image\`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": \`Bearer \${vs_token}\`,
    },
    body: JSON.stringify({
      image_url:  info.srcUrl,
      source_url: tab.url,
      title:      meta.title,
      brand:      meta.brand,
      price:      meta.price,
    }),
  });
});`}</pre>
          </div>
        </div>
      </section>

      {/* Endpoint reference */}
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <SectionLabel>API · clip endpoints</SectionLabel>
        <h2 className="font-heading text-4xl tracking-tight">Two endpoints. Same library.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          <div className="bg-white border border-[#EFEFEF] p-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA47E]">Direct image clip</div>
            <div className="font-heading text-xl mt-2">POST /api/products/clip-image</div>
            <p className="font-body text-sm text-[#595959] mt-3 leading-relaxed">
              Used by the extension when the user right-clicks an exact <code className="font-mono text-xs bg-[#F2EDE3] px-1">img</code> element.
              The extension already knows the image URL, so the backend just persists it with metadata.
            </p>
            <pre className={`${codeBlock} mt-4 text-[11px]`}>{`{
  "image_url":  "https://shop.example.com/v.jpg",
  "source_url": "https://shop.example.com/v",
  "title":      "Velvet Armchair",
  "brand":      "Example Co.",
  "price":      "$1,650",
  "category":   "interior"
}`}</pre>
          </div>
          <div className="bg-white border border-[#EFEFEF] p-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA47E]">Server-side scrape</div>
            <div className="font-heading text-xl mt-2">POST /api/products/clip</div>
            <p className="font-body text-sm text-[#595959] mt-3 leading-relaxed">
              Fallback when the page has no obvious product image. The backend fetches the page
              and extracts <code className="font-mono text-xs bg-[#F2EDE3] px-1">og:image / og:title / og:price</code>.
              Also reachable from the in-app <em>Clip URL</em> dialog.
            </p>
            <pre className={`${codeBlock} mt-4 text-[11px]`}>{`{
  "url": "https://shop.example.com/red-armchair"
}`}</pre>
          </div>
        </div>
      </section>

      {/* Browser support */}
      <section className="bg-white border-t border-[#EFEFEF]">
        <div className="max-w-[1100px] mx-auto px-6 py-16">
          <SectionLabel>◐ Browser support</SectionLabel>
          <h2 className="font-heading text-4xl tracking-tight">Where it runs today.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#EFEFEF]">
            {[
              ["Chrome / Edge / Brave / Arc / Opera", "Ships today", "Loads as an unpacked MV3 extension. Chrome Web Store submission ready.", true],
              ["Firefox", "Drop-in port", "MV3 GA in FF 109+. Add browser_specific_settings + browser-namespace polyfill.", false],
              ["Safari (macOS / iOS 17+)", "Wrap with Xcode", "safari-web-extension-converter. Same source, App Store distribution.", false],
            ].map(([n, status, body, ready], i) => (
              <div key={n} className={`p-6 ${i > 0 ? "md:border-l border-[#EFEFEF]" : ""} ${ready ? "bg-[#333333] text-white" : "bg-white"}`}>
                <div className={`font-mono text-[11px] uppercase tracking-[0.2em] ${ready ? "text-[#9CA47E]" : "text-[#9CA47E]"}`}>{status}</div>
                <div className="font-heading text-2xl mt-2">{n}</div>
                <p className={`font-body text-sm mt-3 leading-relaxed ${ready ? "text-white/70" : "text-[#595959]"}`}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="max-w-[1100px] mx-auto px-6 py-16">
        <SectionLabel>◇ Privacy</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-4xl tracking-tight">No tracking. Clip on demand.</h2>
            <p className="font-body text-[#595959] mt-4 leading-relaxed">
              The extension only reads page metadata when <em>you</em> trigger a clip. Nothing is uploaded
              automatically. Your token sits in <code className="font-mono text-xs bg-[#F2EDE3] px-1">chrome.storage.local</code>
              — extension-only, not exposed to the host page's JavaScript.
            </p>
          </div>
          <ul className="space-y-3 font-body text-sm">
            {[
              "No background page scraping.",
              "No analytics, no telemetry, no fingerprinting.",
              "All traffic goes to your configured VoraStyle API base.",
              "Sign out wipes the local token + cached user.",
            ].map((p) => (
              <li key={p} className="flex gap-3 border-b border-[#EFEFEF] pb-3">
                <ChevronRight className="w-4 h-4 mt-0.5 text-[#9CA47E]" />{p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Big CTA */}
      <section className="bg-[#333333] text-white">
        <div className="max-w-[1100px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            One install. <br />Every retailer<br /> becomes <span className="italic">your library</span>.
          </h2>
          <div className="flex flex-col gap-3 max-w-sm">
            <a href={DOWNLOAD_URL} download="vorastyle-clipper.zip" className="px-5 py-4 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[12px] uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Download Clipper (ZIP)
            </a>
            <Link to="/editor" className="px-5 py-4 bg-transparent border border-white text-white hover:bg-white hover:text-[#333333] font-mono text-[12px] uppercase tracking-[0.18em] text-center transition-colors">
              Open canvas
            </Link>
            <Button asChild variant="ghost" className="text-white/70 font-mono text-[10px] uppercase tracking-[0.2em] hover:text-white">
              <Link to="/support">☕ Support the collective</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
